import React from "react";
import { Control, useController, useForm, useWatch } from "react-hook-form";
import {
  Button,
  Modal,
  Grid,
  Text,
  Divider,
  RadioController,
  DateController,
  NumberController,
} from "@pushpress/shared-components";
import { ClassEndsForm, ClassForm } from "./../CalendarForm.utils";

type ClassEndsModalProps = {
  visible: boolean;
  onClose: VoidFunction;
  control: Control<ClassForm>;
};

const ClassEndsModal = (props: ClassEndsModalProps) => {
  return (
    <Modal visible={props.visible} closeModal={props.onClose} disableClickAway>
      <ClassEndsContent {...props} />
    </Modal>
  );
};

const ClassEndsContent = ({
  onClose,
  control: calendarControl,
}: ClassEndsModalProps) => {
  const initialClassEnds = useWatch({
    control: calendarControl,
    name: "classEnds",
  });
  const classEndsController = useController({
    control: calendarControl,
    name: "classEnds",
  });
  const classEndDateController = useController({
    control: calendarControl,
    name: "date.end",
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<{
    classEnds: ClassEndsForm;
  }>({
    defaultValues: {
      classEnds: initialClassEnds,
    },
  });

  const classEnds = useWatch({
    control,
    name: "classEnds.type",
  });

  const save = (values: { classEnds: ClassEndsForm }) => {
    classEndsController.field.onChange(values.classEnds);
    if (values.classEnds.type === "on") {
      classEndDateController.field.onChange(values.classEnds.date);
    } else {
      classEndDateController.field.onChange(undefined);
    }
    onClose();
  };

  return (
    <div data-cy="class-ends-modal">
      <Modal.Card>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          paddingX={3}
          paddingY={3}
        >
          <Grid item>
            <Text variant="heading-4">Class ends</Text>
          </Grid>
          <Grid item>
            <Grid container>
              <Grid item>
                <Button
                  text="Close"
                  buttonType="secondary"
                  size="medium"
                  onClick={onClose}
                />
              </Grid>
              <Grid item>
                <Button
                  data-cy="save-class-ends-button"
                  text="Save"
                  buttonType="primary"
                  size="medium"
                  type="button"
                  disabled={!isValid}
                  onClick={handleSubmit(save)}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider />
        <Modal.Content>
          <div data-cy="class-ends-type">
            <RadioController
              className="mt-4"
              data-cy="class-ends-type"
              options={[
                {
                  label: "Never",
                  value: "never",
                },
                {
                  label: "On a date",
                  value: "on",
                },
                {
                  label: "After",
                  value: "after",
                },
              ]}
              direction="vertical"
              control={control}
              name="classEnds.type"
              label="Class ends type"
              hideLabel
              required
            />
          </div>
          {classEnds === "on" && (
            <DateController
              className="mb-0"
              name="classEnds.date"
              label="On date"
              control={control}
            />
          )}
          {classEnds === "after" && (
            <NumberController
              className="mb-0"
              name="classEnds.number"
              label="Occurrences"
              control={control}
            />
          )}
        </Modal.Content>
      </Modal.Card>
    </div>
  );
};

export default ClassEndsModal;
